import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Users,
  Settings as SettingsIcon,
  BarChart3,
  LogOut,
  ExternalLink,
  Menu,
  X,
  Tag,
  FileText,
  Inbox,
  Bell,
  User,
  Ticket,
  Star,
  ShoppingCart,
} from "lucide-react";
import { useAdminAuth } from "../../lib/auth";
import api from "../../lib/api";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/orders", icon: Package, label: "Orders" },
  { to: "/admin/products", icon: ShoppingBag, label: "Products" },
  { to: "/admin/categories", icon: Tag, label: "Categories" },
  { to: "/admin/customers", icon: Users, label: "Customers" },
  { to: "/admin/coupons", icon: Ticket, label: "Coupons" },
  { to: "/admin/reviews", icon: Star, label: "Reviews" },
  { to: "/admin/abandoned-carts", icon: ShoppingCart, label: "Abandoned checkouts" },
  { to: "/admin/submissions", icon: Inbox, label: "Inbox" },
  { to: "/admin/content", icon: FileText, label: "Content" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
  { to: "/admin/account", icon: User, label: "Account" },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState({ unread_contact: 0, pending_orders: 0, total: 0 });

  useDocumentMeta({ title: "Flownté · Atelier Console", noindex: true });

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login", { replace: true });
  }, [loading, admin, navigate]);

  useEffect(() => {
    if (!admin) return;
    const load = () => api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [admin]);

  if (loading || !admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-100">
        <div className="font-script text-3xl text-blush-600">loading…</div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const Sidebar = ({ onItemClick }) => (
    <div className="h-full flex flex-col bg-white">
      <div className="p-6 border-b border-blush-100 flex items-center justify-between">
        <div>
          <div className="font-script text-3xl text-blush-600 leading-none">Flownté</div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-ink-500 font-body mt-1">
            Atelier Console
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden h-9 w-9 grid place-items-center rounded-full hover:bg-blush-50"
          aria-label="Close menu"
        >
          <X size={18} strokeWidth={1.6} />
        </button>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {NAV.map((n) => {
          const badge = n.label === "Inbox" ? notifs.unread_contact : n.label === "Orders" ? notifs.pending_orders : 0;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onItemClick}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-full font-body text-sm transition ${
                  isActive
                    ? "bg-blush-50 text-blush-600 font-medium"
                    : "text-ink-700 hover:bg-blush-50/60 hover:text-blush-600"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.6} />
              <span className="flex-1">{n.label}</span>
              {badge > 0 && (
                <span className="h-5 min-w-[1.25rem] px-1 rounded-full bg-blush-600 text-white text-[10px] grid place-items-center">
                  {badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-blush-100 space-y-2">
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-full font-body text-xs text-ink-700 hover:bg-blush-50 transition"
        >
          <ExternalLink size={14} strokeWidth={1.6} />
          View storefront
        </a>
        <div className="px-4 py-2 text-[11px] font-body text-ink-500 truncate">
          {admin.email}
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm text-blush-600 hover:bg-blush-50 transition"
        >
          <LogOut size={14} strokeWidth={1.6} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream-100 text-ink-900 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-blush-100" data-testid="admin-sidebar">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="absolute inset-y-0 left-0 w-72 max-w-[85%]"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-sidebar-mobile"
          >
            <Sidebar onItemClick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-blush-100 px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="admin-menu-btn"
            className="h-10 w-10 grid place-items-center rounded-full hover:bg-blush-50"
            aria-label="Open menu"
          >
            <Menu size={20} strokeWidth={1.6} />
          </button>
          <div className="font-script text-2xl text-blush-600 leading-none">Flownté</div>
          <NavLink to="/admin/submissions" className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-blush-50" data-testid="admin-notif-bell">
            <Bell size={18} strokeWidth={1.6} className="text-ink-700" />
            {notifs.total > 0 && (
              <span className="absolute top-1.5 right-1.5 h-4 min-w-[1rem] px-1 rounded-full bg-blush-600 text-white text-[9px] grid place-items-center">
                {notifs.total}
              </span>
            )}
          </NavLink>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
